
// * ========================================================================
// * Summary chart
// * ========================================================================

import CovidChart from './covid-chart.js';
import moment from 'moment';
import storage from './../storage';

const hospitalAge = (function() {
    const typeSelectorClass = 'input[name=hospital-by-age-chart-data]';
    let thisChart;
    let chartData = null;
    let dataType = null;
    let chartFirstDate = null;
    let typeSelectors = null;

    /**
     *
     * Create new chart
     *
     * @param {Object} data - covid data
     * @param {Object} options - chart options
     * @param {Array} selected - selected options
     *
     * @returns {Object} - chart
     */
    function createChartData(data, options, selected) {
        const datasets = [{ // Guide for LineWithLine
            label: '',
            id: 'hidden-guide',
            backgroundColor: 'rgba(0,0,0,0)',
            borderColor: 'rgba(0,0,0,0)',
            data: [],
            type: 'LineWithLine',
            selected: true,
            order: 0,
            pointRadius: 0
        }];

        const dataOrder = {
            '85': 1,
            '75-84': 2,
            '65-74': 2,
            '55-64': 3,
            '45-54': 4,
            '35-44': 5,
            '25-34': 6,
            '15-24': 7,
            '5-14': 8,
            '0-4': 9
        };

        const ageIdxMap = {};

        for (let day of data.hospital.ages) {
            const newDate = moment(day.date);
            newDate.set({
                hour: 0,
                minute: 0,
                second: 0
            });

            if (newDate >= chartFirstDate) {
                datasets[0].data.push({
                    x: newDate,
                    y: 0
                });

                for (const age of day.ages) {
                    if (age.id === 'all') {
                        continue;
                    }

                    if (typeof ageIdxMap[age.id] === 'undefined') {
                        ageIdxMap[age.id] = datasets.length;
                        datasets.push({
                            label: age.name,
                            id: age.id,
                            backgroundColor: options.chartColors[ageIdxMap[age.id]],
                            borderColor: options.chartColors[ageIdxMap[age.id]],
                            labelColor: options.chartColors[ageIdxMap[age.id]],
                            data: [],
                            type: 'LineWithLine',
                            selected: selected && Array.isArray(selected) ? selected.indexOf(age.id) !== -1 : true,
                            order: dataOrder[age.id],
                            pointRadius: 0,
                            tension: 0.2
                        });
                    }

                    let count;
                    if (dataType === 'due') {
                        count = age.forCovidPrimary + age.forCovidSpecial;
                    } else if (dataType === 'with') {
                        count = age.withCovidPrimary + age.withCovidSpecial;
                    } else {
                        count = age.hospitalized;
                    }

                    datasets[ageIdxMap[age.id]].data.push({
                        x: newDate,
                        y: count
                    });

                }
            }
        }

        // Dotted
        const lag = 4; // weeks
        const dataLagIdx = datasets[0].data.length - lag;
        const dashed = function (ctx, value) {
            if (ctx.p0DataIndex > dataLagIdx) {
                return value;
            }
            return undefined;
        }

        for (let dataset of datasets) {
            dataset.segment = {
                borderDash: ctx => dashed(ctx, [6, 6]),
            };
        }

        return datasets;
    }

    function getDataType(initialType = null) {
        let initialCheckbox = null;
        if (initialType) {
            initialCheckbox = document.querySelector(typeSelectorClass + '[value="'+initialType+'"]');
        }

        if (initialCheckbox) {
            initialCheckbox.checked = true;
            dataType = initialType;
            return dataType;
        }

        dataType = document.querySelector(typeSelectorClass+':checked').value;

        return dataType;
    }

    /**
     * Initialize
     *
     * @param {Object} data - covid data
     */
    function init(data) {
        chartData = data;
        chartFirstDate = window.config.firstDayValues['hospital'];

        typeSelectors = document.querySelectorAll(typeSelectorClass);

        const initialType = storage.getData(typeSelectorClass);

        // Get the data type we want to show
        getDataType(initialType);

        // Initially selected
        let selectedAges = storage.getData('hospital-by-age-chart_multiselect');
        if (!selectedAges || !Array.isArray(selectedAges)) {
            selectedAges = null;
        }

        const datasets = createChartData(chartData, window.config, selectedAges);

        thisChart = new CovidChart('hospital-by-age-chart', {
            datasets,
            chartOptions: {
                options: {
                    legend: {
                        display: true,
                        labels: {
                            boxWidth: 12
                        }
                    },
                    plugins: {
                        tooltip: {
                            position: 'nearest',
                            intersect: false,
                            bodySpacing: 5,
                            itemSort: function(a, b, data) {
                                return b.parsed.y > a.parsed.y ? 1 : -1;
                            },
                        },
                    },
                    scales: {
                        x: {
                            type: 'time',
                            display: true,
                            time: {
                                unit: 'day',
                                displayFormats: {
                                    day: 'D.M.YY',
                                }
                            },
                            ticks: {
                                major: {
                                    fontStyle: 'bold',
                                    fontColor: '#FF0000'
                                }
                            }
                        },
                        y: {
                            stacked: false,
                            min: 0,
                            type: 'linear',
                            position: 'left',
                        }
                    }
                }
            },
            delayAnnotation: true,
            config: window.config,
            tooltips: {
                elementId: 'hospital-by-age-chart-tooltip'
            },
            multiselect: {
                requireCount: false,
                valueType: 'string',
                idField: 'id',
                colorField: 'labelColor',
                inputName: 'hospital-by-age-multiselect',
                selectElemId: 'hospital-by-age-multiselect',
                colorElemId: 'hospital-by-age-multiselect-colors',
                dropdownId: 'hospital-by-age-multiselect-dropdown',
                selectAllElemId: 'hospital-by-age-multiselect-select-all',
                clearAllElemId: 'hospital-by-age-multiselect-clear-all',
                countElemId: 'hospital-by-age-multiselect-count'
            }
        });

        // Create listener for type change
        typeSelectors.forEach((typeSelector) => {
            typeSelector.addEventListener('change', () => {
                let newType = document.querySelector(typeSelectorClass+':checked').value;
                storage.updateData(typeSelectorClass, newType);
                if (dataType !== newType) {
                    reload(null, true);
                }
            });
        });
    }

    function reload(data = null, changeType = false) {
        if (data) {
            chartData = data;
        }

        // Get the data type we want to show
        getDataType();

        if (!changeType) {
            // New first date value
            chartFirstDate = window.config.firstDayValues['hospital'];
        }

        const datasets = createChartData(chartData, window.config);
        thisChart.updateDatasets(datasets);
    }

    return {
        init,
        reload
    }

})();

export default hospitalAge;
